// Read-only client for the sksis database exposed via PostgREST (data.sksis.ca).
//
// PostgREST returns a bare JSON array (`[...]`) whereas the legacy DreamFactory
// API wrapped rows as `{ resource: [...] }`. To keep the migration small and
// low-risk, pgSelect re-wraps results in the same `{ resource: [...] }` shape so
// existing consumers that read `.resource` continue to work unchanged.
//
// NOTE: this endpoint is READ-ONLY. Writes (create/update/delete) and stored
// procedure calls still go through DreamFactory in the other services.

export const POSTGREST_BASE = 'https://data.sksis.ca';

export interface PgResult<T = any> {
  resource: T[];
}

/**
 * Build a PostgREST `column=eq.value` filter. The column name is passed through
 * verbatim (PostgREST is case-sensitive — e.g. `Assoc`, `Mapunit_and_Modifier`),
 * while the value is URL-encoded.
 */
export function pgEq(column: string, value: string | number): string {
  return `${column}=eq.${encodeURIComponent(String(value))}`;
}

/**
 * Fetch rows from a table/view. `query` is a raw PostgREST query string
 * (e.g. `poly_id=eq.123&select=soilname,percent`). Returns the rows wrapped as
 * `{ resource: [...] }` for DreamFactory compatibility.
 */
export async function pgSelect<T = any>(
  table: string,
  query = '',
  init?: RequestInit,
): Promise<PgResult<T>> {
  const url = query ? `${POSTGREST_BASE}/${table}?${query}` : `${POSTGREST_BASE}/${table}`;
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.headers || {}),
    },
  });
  if (!response.ok) {
    throw new Error(`PostgREST request failed for ${table}: ${response.status}`);
  }
  const rows = await response.json();
  return { resource: Array.isArray(rows) ? rows : [] };
}

/**
 * Call a database function via PostgREST RPC (`POST /rpc/<fn>`). Arguments are
 * matched to the function's parameters by name. Functions declared
 * `RETURNS TABLE(...)` come back as a JSON array of rows, so callers typically
 * read `result[0]`.
 */
export async function pgRpc<T = any>(
  fn: string,
  args: Record<string, unknown> = {},
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${POSTGREST_BASE}/rpc/${fn}`, {
    method: 'POST',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init?.headers || {}),
    },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    throw new Error(`PostgREST rpc ${fn} failed: ${response.status}`);
  }
  return (await response.json()) as T;
}
